import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  HStack,
  IconButton,
  Input,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
  useMediaQuery,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppRoutes } from "src/common/constants/constantEnums";
import MatrimonyNavbar from "src/pages/home/matrimonyNavbar";
import FilterIcon from "src/resources/icons/FilterIcon";
import { MarriageProfileService } from "src/rest/marriageProfileService";
import { FiltersPanel, ProfileCard } from "./matrimonySearchResults.components";
import { UseAppToast } from "src/common/useAppToast";
import { ConnectionsService } from "src/rest/connectionsService";
import { GlobalUtils } from "src/utils/globalUtils";
import { ProfileService } from "src/rest/profileService";
import type { ProfileDto } from "src/types/Profile";

import type { MarriageProfileSearchItem, PagedResult, SearchFilters } from "src/types/matrimonySearchResults.types";
import {
  clamp,
  deserializeParamsToFilters,
  getResults,
  getTotal,
  serializeFiltersToParams,
  stableFilterKey,
  toBackendGender,
  toBackendMaritalStatus,
  toFrontendGender,
  toFrontendMaritalStatus,
  toUiPage,
  toApiPage,
} from "src/utils/matrimonySearchResults.utils";

export default function MatrimonySearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showErrorToast } = UseAppToast();
  const navigate = useNavigate();

  // Drawer only on true mobile widths. Tablets/laptops should always keep 2-column layout.
  // Theme breakpoints are customized (sm=375px), so we prefer a stable media query.
  const [isMobile] = useMediaQuery("(max-width: 767px)");
  const drawer = useDisclosure();

  // Client-side search within already fetched results.
  const [resultsQuery, setResultsQuery] = useState("");

  // Applied filters are the source of truth for URL + API.
  // Keep them, but DO NOT auto-fetch on change.
  const [appliedFilters, setAppliedFilters] = useState<SearchFilters>(() =>
    deserializeParamsToFilters(searchParams, {
      toFrontendGenderImpl: toFrontendGender,
      toFrontendMaritalStatusImpl: toFrontendMaritalStatus,
    })
  );
  // Draft filters are edited locally and only applied on explicit action.
  const [draftFilters, setDraftFilters] = useState<SearchFilters>(() =>
    deserializeParamsToFilters(searchParams, {
      toFrontendGenderImpl: toFrontendGender,
      toFrontendMaritalStatusImpl: toFrontendMaritalStatus,
    })
  );

  const [data, setData] = useState<PagedResult<MarriageProfileSearchItem> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch logged-in user's own marriage profile id (login info only has email/mobile)
  const [loggedInMarriageProfileId, setLoggedInMarriageProfileId] = useState<string | undefined>(undefined);

  // Set of receiverProfileIds to which a PENDING request has already been sent.
  const [pendingReceiverIds, setPendingReceiverIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const emailId = GlobalUtils.getUserLoginInfo()?.emailId || "";
        if (!emailId) return;

        const profile = (await ProfileService.getProfile(emailId)) as ProfileDto;
        const firstId = (profile as any)?.marriageProfiles?.[0]?.id;
        if (!cancelled) setLoggedInMarriageProfileId(firstId == null ? undefined : String(firstId));
      } catch {
        if (!cancelled) setLoggedInMarriageProfileId(undefined);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Load all sent requests once we know the logged-in profile id.
  // Build a Set of receiverProfileIds that are currently PENDING.
  useEffect(() => {
    if (!loggedInMarriageProfileId) return;
    let cancelled = false;

    (async () => {
      try {
        const sent = await ConnectionsService.getSentRequests(loggedInMarriageProfileId);
        if (cancelled) return;
        const ids = new Set(
          (sent as any[])
            .filter((r) => String(r?.status || "").toUpperCase() === "PENDING")
            .map((r) => String(r?.receiverProfileId ?? ""))
            .filter(Boolean)
        );
        setPendingReceiverIds(ids);
      } catch {
        // Non-fatal — leave the set empty so the UI degrades gracefully.
      }
    })();

    return () => { cancelled = true; };
  }, [loggedInMarriageProfileId]);

  // Keep state in sync with URL (back/forward, external nav)
  useEffect(() => {
    const next = deserializeParamsToFilters(searchParams, {
      toFrontendGenderImpl: toFrontendGender,
      toFrontendMaritalStatusImpl: toFrontendMaritalStatus,
    });
    if (stableFilterKey(next) !== stableFilterKey(appliedFilters)) {
      setAppliedFilters(next);
      setDraftFilters(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const apiParams = useMemo(() => {
    return {
      gender: toBackendGender(appliedFilters.gender),
      maritalStatus: toBackendMaritalStatus(appliedFilters.maritalStatus),
      nativePlace: appliedFilters.nativePlace,
      highestEducation: appliedFilters.highestEducation,
      employmentSector: appliedFilters.employmentSector,
      dietaryHabits: appliedFilters.dietaryHabits,
      smoking: appliedFilters.smoking,
      drinking: appliedFilters.drinking,
      minAge: appliedFilters.minAge,
      maxAge: appliedFilters.maxAge,
      minHeight: appliedFilters.minHeight,
      maxHeight: appliedFilters.maxHeight,
      minIncome: appliedFilters.minIncome,
      maxIncome: appliedFilters.maxIncome,
      dosham: appliedFilters.dosham,
      hasPhoto: appliedFilters.hasPhoto,
      page: Math.max(0, Number(appliedFilters.page) || 0),
      size: appliedFilters.size,
    } as Record<string, any>;
  }, [appliedFilters]);

  // Keep a ref to the latest apiParams so the fetch effect can read the
  // current value without listing apiParams as a dependency (which would
  // cause a fetch on every filter-draft keystroke before Apply is pressed).
  const apiParamsRef = useRef(apiParams);
  apiParamsRef.current = apiParams;

  // Only update the URL based on applied filters.
  const applyToUrl = (next: SearchFilters) => {
    setSearchParams(
      serializeFiltersToParams({ ...next, q: next.q?.trim() || undefined }, {
        toBackendGenderImpl: toBackendGender,
        toBackendMaritalStatusImpl: toBackendMaritalStatus,
      }),
      { replace: false }
    );
  };

  // Apply button: commit draft -> applied, reset to page 0, and update URL.
  const applyFilters = () => {
    const nextApplied: SearchFilters = {
      ...draftFilters,
      page: 0,
      size: appliedFilters.size || draftFilters.size || 10,
    };

    setAppliedFilters(nextApplied);
    setDraftFilters(nextApplied);
    applyToUrl(nextApplied);
    if (isMobile) drawer.onClose();
  };

  const clearAll = () => {
    const cleared: SearchFilters = {
      gender: undefined,
      maritalStatus: undefined,
      nativePlace: undefined,
      highestEducation: undefined,
      employmentSector: undefined,
      dietaryHabits: undefined,
      smoking: undefined,
      drinking: undefined,
      minAge: undefined,
      maxAge: undefined,
      minHeight: undefined,
      maxHeight: undefined,
      minIncome: undefined,
      maxIncome: undefined,
      dosham: undefined,
      hasPhoto: undefined,
      q: undefined,
      page: 0,
      size: appliedFilters.size || draftFilters.size || 10,
    };

    setAppliedFilters(cleared);
    setDraftFilters(cleared);
    setResultsQuery("");
    applyToUrl(cleared);
    if (isMobile) drawer.onClose();
    setApplyToken((t) => t + 1);
  };

  // Fetch: only when an explicit apply token changes (apply button or pagination).
  const [applyToken, setApplyToken] = useState(1);

  const applyFiltersAndFetch = () => {
    applyFilters();
    setApplyToken((t) => t + 1);
  };

  // Pagination: allowed to apply immediately (this should trigger fetch).
  const goToPage = (uiPage1: number) => {
    const nextApplied = {
      ...appliedFilters,
      page: clamp(toApiPage(uiPage1), 0, Math.max(0, totalPages - 1)),
    };
    setAppliedFilters(nextApplied);
    setDraftFilters((d) => ({ ...d, page: nextApplied.page, size: nextApplied.size }));
    applyToUrl(nextApplied);
    setApplyToken((t) => t + 1);
  };

  // Stable ref for showErrorToast so it never appears as a fetch dep.
  const showErrorToastRef = useRef(showErrorToast);
  showErrorToastRef.current = showErrorToast;

  // Fetch only when applyToken changes or loggedInMarriageProfileId first resolves.
  // apiParamsRef.current is always up-to-date without being a dep (avoids
  // re-fetching on every draft-filter keystroke before Apply is pressed).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        if (!loggedInMarriageProfileId) {
          // Backend requires /search/{yourProfileId} — wait until resolved.
          setData({ content: [], totalElements: 0 });
          return;
        }
        const res = await MarriageProfileService.search(
          loggedInMarriageProfileId,
          apiParamsRef.current
        );
        if (cancelled) return;
        setData(res);
      } catch (err: any) {
        if (cancelled) return;
        showErrorToastRef.current(err?.message || "Something went wrong.");
        setData({ content: [], totalElements: 0 });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyToken, loggedInMarriageProfileId]);

  // Derive results — kept inside useMemo so refs are stable across renders.
  const resultsWithoutSelf = useMemo(() => {
    const raw = data ? getResults(data) : [];
    if (!loggedInMarriageProfileId) return raw;
    return raw.filter((p) => String((p as any)?.id) !== loggedInMarriageProfileId);
  }, [data, loggedInMarriageProfileId]);

  const total = useMemo(() => (data ? getTotal(data) : 0), [data]);

  const filteredResults = useMemo(() => {
    const q = resultsQuery.trim().toLowerCase();
    if (!q) return resultsWithoutSelf;
    return resultsWithoutSelf.filter((p) => (p.fullName || "").toLowerCase().includes(q));
  }, [resultsWithoutSelf, resultsQuery]);

  const searchingText = appliedFilters.q ? `Searching for the term “${appliedFilters.q}”` : "";

  const onSeeDetails = (id: string | number) => {
    navigate(`${AppRoutes.MATRIMONY_PROFILE_DETAILS}?id=${encodeURIComponent(String(id))}`);
  };

  const totalPages = useMemo(() => {
    const s = appliedFilters.size || 10;
    return Math.max(1, Math.ceil(total / s));
  }, [appliedFilters.size, total]);

  const FiltersContent = (
    <FiltersPanel
      value={draftFilters}
      onChange={setDraftFilters}
      onApply={applyFiltersAndFetch}
      onClear={clearAll}
      isMobile={isMobile}
    />
  );

  return (
    <Box minH="100vh" bg="#FBF7F4">
      <Box position="sticky" top={0} zIndex={50}>
        <MatrimonyNavbar isScrolled={true} activeSection="" />
      </Box>

      <Box maxW="7xl" mx="auto" px={{ base: 4, md: 6, xl: 6 }} py={{ base: 4, md: 6 }}>
        <Flex w="full" gap={{ base: 0, md: 6 }} align="flex-start" minW={0}>
          {/* Left filters (desktop/tablet only) */}
          {!isMobile ? (
            <Box
              w={{ md: "280px", lg: "300px", xl: "340px"}}
              flexShrink={0}
              position="sticky"
              top={{ base: 0, md: "84px" }}
              alignSelf="flex-start"
              maxH={{ md: "calc(100vh - 110px)" }}
              overflowY={{ md: "auto" }}
              marginTop={20}
            >
              {FiltersContent}
            </Box>
          ) : null}

          {/* Right results */}
          <Box flex="1" minW={0} marginTop={20}>
            {/* Header: desktop = search in results + Apply, mobile = search in results + filter icon */}
            <Flex align={{ base: "stretch", md: "center" }} gap={3} mb={4} direction={{ base: "row", md: "row" }}>
              <Input
                bg="#F8F9FA"
                borderRadius="12px"
                borderColor="rgba(108, 117, 125, 0.3)"
                height="44px"
                placeholder="Search in results"
                value={resultsQuery}
                onChange={(e) => setResultsQuery(e.target.value)}
                aria-label="Search in results"
              />

              {isMobile ? (
                <IconButton
                  aria-label="Open filters"
                  onClick={drawer.onOpen}
                  icon={<FilterIcon />}
                  borderRadius="12px"
                  variant="outline"
                  flexShrink={0}
                />
              ) : (
                <Button
                  bg="#4A2C2A"
                  color="white"
                  _hover={{ bg: "#3A211F" }}
                  borderRadius="12px"
                  onClick={applyFiltersAndFetch}
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  Apply
                </Button>
              )}
            </Flex>

            {searchingText ? (
              <Text fontSize="sm" color="#6C757D" mb={4}>
                {searchingText}
              </Text>
            ) : null}

            {isLoading ? (
              <Stack spacing={4}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} height="150px" borderRadius="16px" />
                ))}
              </Stack>
            ) : filteredResults.length > 0 ? (
              <Grid templateColumns="1fr" gap={{ base: 4, md: 5 }}>
                {filteredResults.map((p) => (
                  <ProfileCard
                    key={String(p.id)}
                    profile={p}
                    onSeeDetails={onSeeDetails}
                    isPending={pendingReceiverIds.has(String(p.id))}
                  />
                ))}
              </Grid>
            ) : (
              <Box
                bg="#FFFFFF"
                border="1px solid rgba(108, 117, 125, 0.18)"
                borderRadius="16px"
                p={6}
                textAlign="center"
              >
                <Text fontWeight="700" color="#1F0812">
                  No profiles found
                </Text>
                <Text color="#6C757D" mt={1}>
                  Try adjusting filters.
                </Text>
              </Box>
            )}

            <Flex align="center" justify="center" mt={6} gap={2} flexWrap="wrap">
              <Button
                variant="ghost"
                onClick={() => goToPage(toUiPage(appliedFilters.page) - 1)}
                isDisabled={appliedFilters.page <= 0}
              >
                ‹
              </Button>

              {Array.from({ length: Math.min(7, totalPages) }).map((_, idx) => {
                const pageNum = idx + 1;
                const isActive = pageNum === toUiPage(appliedFilters.page);
                return (
                  <Button
                    key={pageNum}
                    variant={isActive ? "solid" : "ghost"}
                    bg={isActive ? "rgba(74,44,42,0.10)" : undefined}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              <Button
                variant="ghost"
                onClick={() => goToPage(toUiPage(appliedFilters.page) + 1)}
                isDisabled={appliedFilters.page >= totalPages - 1}
              >
                ›
              </Button>
            </Flex>
          </Box>

          {/* Mobile drawer */}
          <Drawer isOpen={drawer.isOpen} placement="right" onClose={drawer.onClose} size="full">
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader>Filters</DrawerHeader>
              <DrawerBody>{FiltersContent}</DrawerBody>
              <DrawerFooter>
                <HStack w="full" spacing={3}>
                  <Button variant="outline" w="full" onClick={clearAll}>
                    Clear all
                  </Button>
                  <Button bg="#4A2C2A" color="white" _hover={{ bg: "#3A211F" }} w="full" onClick={applyFiltersAndFetch}>
                    Apply
                  </Button>
                </HStack>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </Flex>
      </Box>
    </Box>
  );
}
